import { useState } from "react";
import clsx from "clsx";
import { PlaneLanding, PlaneTakeoff } from "lucide-react";
import type { Airline, AirportConfig } from "@yb/core";
import { airlineColor, readableOn } from "../lib/format";

/**
 * An airline as a mark: its logo where an administrator pasted one, otherwise a tile in
 * its colour carrying its code, which is how the board already paints it. One component
 * so every page shows the same airline the same way.
 */

/** The airline a flight's code names, whichever of its codes the feed used. */
export function findAirline(code: string, config: AirportConfig | undefined): Airline | undefined {
  if (!config) return undefined;
  const upper = code.toUpperCase();
  return config.airlines[upper] ?? Object.values(config.airlines).find((a) => a.iata?.toUpperCase() === upper);
}

export function AirlineMark({ code, config, size = "sm", withName = false }: { code: string; config: AirportConfig | undefined; size?: "sm" | "md"; withName?: boolean }) {
  // A logo that fails to load gives way to the tile rather than a broken image.
  const [broken, setBroken] = useState(false);
  const a = findAirline(code, config);
  const name = a?.name ?? code;
  const box = size === "sm" ? "size-5 text-[10px]" : "size-7 text-[11px]";
  const logo = a?.logoUrl && !broken;

  const mark = logo ? (
    <img src={a.logoUrl} alt={name} className={clsx(box, "shrink-0 rounded object-contain")} loading="lazy" referrerPolicy="no-referrer" onError={() => setBroken(true)} />
  ) : (
    <Tile code={a?.iata || a?.code || code} color={a?.color ?? airlineColor(code, config)} name={name} box={box} />
  );

  if (!withName) return mark;
  return (
    <span className="inline-flex items-center gap-1.5">
      {mark}
      <span className="font-semibold">{name}</span>
    </span>
  );
}

/** The fallback: the code on the airline's colour, in whichever ink can be read on it. Grey for an airline Settings does not know. */
function Tile({ code, color, name, box }: { code: string; color: string; name: string; box: string }) {
  return (
    <span className={clsx(box, "inline-flex shrink-0 items-center justify-center rounded font-bold leading-none")} style={{ background: color, color: readableOn(color) }} title={name}>
      {code}
    </span>
  );
}

/**
 * Which way a flight is going: a landing plane for an arrival, a climbing one for a
 * departure, in the colours the Flights per day chart stacks them in (blue arrivals,
 * green departures). The board's own arrows carry no colour, they take the puck's ink,
 * so this is where the two direction colours are set.
 */
export function Direction({ dir, withWord = false }: { dir: "ARR" | "DEP"; withWord?: boolean }) {
  const arr = dir === "ARR";
  const Icon = arr ? PlaneLanding : PlaneTakeoff;
  const title = arr ? "Arrival" : "Departure";
  return (
    <span className={clsx("inline-flex items-center gap-1", arr ? "text-info" : "text-ok")} title={title}>
      <Icon className="size-3.5 shrink-0" aria-label={title} />
      {withWord && <span className="text-ink-2">{arr ? "Arr" : "Dep"}</span>}
    </span>
  );
}
